import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchDecisionInsight,
  fetchRunDetail,
  fetchSkippedTestsAnalysis,
  type RunDetail,
  type SkippedTestsSummary,
} from '../../../api/runs';
import type { RunSummary } from '../utils/resultsFormatting';

export type ExpandedRunDetail = RunDetail & {
  decisionInsight?: any;
  skippedTestsSummary?: SkippedTestsSummary;
};

const loadDecisionInsightIfFunctional = async (
  targetRunId: string,
  runType?: 'chaos' | 'functional', signal?: AbortSignal
) => {
  if (runType !== 'functional') {
    return undefined;
  }

  try {
    return await fetchDecisionInsight(targetRunId, signal);
  } catch (loadError) {
    console.error('Failed to load decision insight:', loadError);
    return undefined;
  }
};

const loadSkippedTestsSummaryIfFunctional = async (
  targetRunId: string,
  runType?: 'chaos' | 'functional', signal?: AbortSignal
) => {
  if (runType !== 'functional') {
    return undefined;
  }

  try {
    const analysis = await fetchSkippedTestsAnalysis(targetRunId, {
      page: 1,
      page_size: 1,
    }, signal);

    return analysis.summary;
  } catch (loadError) {
    console.error('Failed to load skipped tests summary:', loadError);
    return undefined;
  }
};

export const useRunExpansion = ({
  runs,
  projectId,
  runId,
  searchParams,
}: {
  runs: RunSummary[];
  projectId?: string;
  runId?: string;
  searchParams: URLSearchParams;
}) => {
  const [expandedRuns, setExpandedRuns] = useState<Record<string, ExpandedRunDetail>>({});
  const [visibleExpandedRuns, setVisibleExpandedRuns] = useState<Set<string>>(new Set());
  const [loadingRunIds, setLoadingRunIds] = useState<Set<string>>(new Set());
  const [runErrors, setRunErrors] = useState<Record<string, string>>({});
  const controllers = useRef<Record<string, AbortController>>({});
  const [showGraphRunId, setShowGraphRunId] = useState<string | null>(null);
  const projectRef = useRef(projectId);
  const scopeGenerationRef = useRef(0);
  if (projectRef.current !== projectId) scopeGenerationRef.current += 1;
  projectRef.current = projectId;
  useEffect(() => {
    setRunErrors({});
    setExpandedRuns({});
    expandedRunsRef.current = {};
    pendingLoadsRef.current = {};
    setVisibleExpandedRuns(new Set());
    setLoadingRunIds(new Set());
    setShowGraphRunId(null);
    autoExpandedRunRef.current = null;
    return () => {
      scopeGenerationRef.current += 1;
      Object.values(controllers.current).forEach(controller => controller.abort());
      controllers.current = {};
    };
  }, [projectId]);
  const autoExpandedRunRef = useRef<string | null>(null);
  const expandedRunsRef = useRef<Record<string, ExpandedRunDetail>>({});
  const pendingLoadsRef = useRef<Record<string, Promise<ExpandedRunDetail>>>({});
  const runTypeByIdRef = useRef<Record<string, 'chaos' | 'functional' | undefined>>({});

  useEffect(() => {
    expandedRunsRef.current = expandedRuns;
  }, [expandedRuns]);

  useEffect(() => {
    runTypeByIdRef.current = runs.reduce<Record<string, 'chaos' | 'functional' | undefined>>(
      (accumulator, run) => {
        accumulator[run.run_id] = run.run_type;
        return accumulator;
      },
      {}
    );
  }, [runs]);

  const loadRunDetail = useCallback(
    async (targetRunId: string, showWhenLoaded = false) => {
      const requestedProject = projectRef.current;
      const generation = scopeGenerationRef.current;
      const isCurrent = () => scopeGenerationRef.current === generation;
      const cached = expandedRunsRef.current[targetRunId];
      if (cached) {
        if (showWhenLoaded) {
          setVisibleExpandedRuns((prev) => new Set(prev).add(targetRunId));
        }
        return cached;
      }

      if (!pendingLoadsRef.current[targetRunId]) {
        const runType = runTypeByIdRef.current[targetRunId];
        const controller = new AbortController();
        controllers.current[targetRunId] = controller;

        pendingLoadsRef.current[targetRunId] = (async () => {
          setLoadingRunIds((prev) => new Set(prev).add(targetRunId));
          setRunErrors(previous => ({ ...previous, [targetRunId]: '' }));
          try {
            const [details, insight, skippedTestsSummary] = await Promise.all([
              fetchRunDetail(targetRunId, runType === 'functional' ? { view: 'summary' } : undefined, controller.signal),
              loadDecisionInsightIfFunctional(targetRunId, runType, controller.signal),
              loadSkippedTestsSummaryIfFunctional(targetRunId, runType, controller.signal),
            ]);
            if (!isCurrent() || details.run_id !== targetRunId || (requestedProject && details.project_id && details.project_id !== requestedProject)) {
              throw new Error('Run response does not match the active run and project');
            }
            const fullDetails = {
              ...details,
              decisionInsight: insight,
              skippedTestsSummary,
            };

            if (!isCurrent()) throw new Error('Run scope changed');
            setExpandedRuns((prev) => ({
              ...prev,
              [targetRunId]: fullDetails,
            }));
            return fullDetails;
          } catch (error) {
            controller.abort();
            if (isCurrent()) setRunErrors(previous => ({ ...previous, [targetRunId]: 'Saved run details could not be loaded. Retry the read.' }));
            throw error;
          } finally {
            if (isCurrent()) delete controllers.current[targetRunId];
            if (isCurrent()) delete pendingLoadsRef.current[targetRunId];
            if (isCurrent()) setLoadingRunIds((prev) => {
              const next = new Set(prev);
              next.delete(targetRunId);
              return next;
            });
          }
        })();
      }

      const loaded = await pendingLoadsRef.current[targetRunId];
      if (showWhenLoaded && isCurrent()) {
        setVisibleExpandedRuns((prev) => new Set(prev).add(targetRunId));
      }
      return loaded;
    },
    []
  );

  useEffect(() => {
    const targetRunId = searchParams.get('runId') || runId;

    if (!targetRunId || !runs.length) {
      return;
    }

    if (autoExpandedRunRef.current === targetRunId) {
      return;
    }

    const runInCurrentPage = runs.find((run) => run.run_id === targetRunId);

    if (!runInCurrentPage) {
      return;
    }

    if (!expandedRunsRef.current[targetRunId]) {
      (async () => {
        try {
          await loadRunDetail(targetRunId, true);
          autoExpandedRunRef.current = targetRunId;
          setTimeout(() => {
            const element = document.getElementById(`run-${targetRunId}`);

            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 200);
        } catch (loadError) {
          console.error('Failed to load run:', loadError);
        }
      })();
    } else {
      setVisibleExpandedRuns((prev) => new Set(prev).add(targetRunId));
      autoExpandedRunRef.current = targetRunId;

      setTimeout(() => {
        const element = document.getElementById(`run-${targetRunId}`);

        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);
    }
  }, [searchParams, runId, runs, loadRunDetail]);

  const toggleExpand = async (targetRunId: string) => {
    const isCurrentlyExpanded = visibleExpandedRuns.has(targetRunId);

    if (isCurrentlyExpanded) {
      setVisibleExpandedRuns((prev) => {
        const newSet = new Set(prev);
        newSet.delete(targetRunId);
        return newSet;
      });

      setShowGraphRunId(null);
      return;
    }

    if (!expandedRuns[targetRunId]) {
      try {
        setVisibleExpandedRuns((prev) => new Set(prev).add(targetRunId));
        await loadRunDetail(targetRunId);
      } catch (loadError) {
        console.error('Failed to load run details:', loadError);
        return;
      }

      return;
    }

    setVisibleExpandedRuns((prev) => new Set(prev).add(targetRunId));
  };

  return {
    expandedRuns,
    visibleExpandedRuns,
    loadingRunIds,
    runErrors,
    retryRunDetail: (targetRunId: string) => loadRunDetail(targetRunId).catch(() => undefined),
    showGraphRunId,
    setShowGraphRunId,
    toggleExpand,
  };
};
